/**
 * TanStack Query hooks for the user profile and training programs.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../client'
// A3.1b codegen spike: this one mutation takes its request type from the
// generated schema instead of a hand-written stand-in. `Partial<UserProfileOut>`
// used the *response* type as the request type, which could neither send
// `gym_name` nor `training_start_date` (both accepted by the backend) and let
// `id` and `programs` through (both silently ignored — there is no
// `extra="forbid"`).
import type { components } from '../generated/schema'
import type {
  UserProfileOut,
  TrainingProgramOut,
  TrainingProgramUpdate,
} from '../types'

// ── User Profile ─────────────────────────────────────────────────────

/** User profile — name, goals, and training mission. */
export function useProfile() {
  return useQuery({
    queryKey: ['profile'],
    queryFn: () => apiFetch<UserProfileOut>('/api/profile'),
  })
}

/** Partial-update the user profile. Invalidates ['profile'] on success. */
export function useUpdateProfile() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: components['schemas']['UserProfileUpdate']) =>
      apiFetch<UserProfileOut>('/api/profile', {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] })
    },
  })
}

// ── Training Programs ────────────────────────────────────────────────

/** Update an existing training program. Invalidates ['profile', 'programs']. */
export function useUpdateProgram() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: TrainingProgramUpdate }) =>
      apiFetch<TrainingProgramOut>(`/api/profile/programs/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] })
      queryClient.invalidateQueries({ queryKey: ['programs'] })
    },
  })
}
